import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaTruck, FaTimesCircle, FaBoxOpen } from 'react-icons/fa';
import '../../Components/Orders/Orders.css';
import { useUser } from '../../store/UserContext';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/Config';
import { useNavigate } from 'react-router-dom';
import LoadingForAll from '../../Pages/LoadingForAll';
import SvgImg1 from './SvgImg1';

function Orders() {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {

    const fetchOrders = async () => {
      
      try {
        if (!currentUser) {
          navigate('/login');
          return;
        }

        const ordersQuery = query(collection(db, 'orders'), where('userId', '==', currentUser.uid));
        const ordersSnapshot = await getDocs(ordersQuery);
        const ordersData = ordersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort orders by orderDate descending (latest first)
        ordersData.sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));
        setOrders(ordersData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [currentUser, navigate]);

  if (loading) return <LoadingForAll title="Loading your orders..." />;
  if (error) return <div className="error-message">Error: {error}</div>;
  if (!currentUser) return <div>Redirecting to login...</div>;

  return (
    <div className="orders-container">
    <div className="orders-header">
      <h1 className="orders-title">Your Order History</h1>
      <p className="orders-subtitle">Track your purchases and shipment status</p>
      <div className="header-decoration"></div>
    </div>
  
    {orders.length === 0 ? (
      <div className="empty-orders">
        <div className="empty-icon-container">
          <FaBoxOpen className="empty-icon" />
        </div>
        <h2>No Orders Yet</h2>
        <p>Your order history is currently empty. Start shopping to see your orders here.</p>
        <button className="browse-btn" onClick={() => navigate('/')}>
          Start Shopping
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13 7H21V9H13V7ZM13 11H21V13H13V11ZM13 15H21V17H13V15ZM5 20L0 12L5 4H7V12H9V4H11L16 12L11 20H9V12H7V20H5Z" fill="currentColor"/>
          </svg>
        </button>
      </div>
    ) : (
      <div className="orders-list">
        {orders.map((order) => (
          <div className="order-card" key={order.id}>
            <div className="order-header">
              <div className="order-meta">
                <div className="order-date">
                 <SvgImg1> </SvgImg1>
                  {new Date(order.orderDate).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
                
              </div>
              <div className="order-status-container">
                      <button 
                          className="action-btn help-btn"
                          onClick={() => navigate('/contact-us')}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Get Help
                        </button>
                        
                <span className={`status-badge ${order.orderStatus.toLowerCase()}`}>
                  {order.orderStatus}
                </span>
                <span className="order-total">
                  ₹{order.totalAmount.toLocaleString()}
                </span>
              </div>
            </div>
  
            <div className="order-items">
              {order.productsDetails.length > 0 ? (
                order.productsDetails.map((product, index) => (
                  <div className="order-item" key={index}>
                    <div className="item-image-container">
                      <img 
                        src={product.productImage} 
                        alt={product.productName} 
                        className="item-image"
                        onClick={() => navigate(`/view-product-details/${product.productId}`)}
                      />
                    </div>
                    <div className="item-info">
                      <h3 className="item-title" onClick={() => navigate(`/view-product-details/${product.productId}`)}>
                       {product.productName}
                      </h3>
                      <div className="item-details">
                        <div className="item-quantity">Per Unit: ₹{product.productPrice?.toLocaleString()}</div>
                        <div className="item-quantity">Quantity: {product.productQuantity}</div>
                        <div className="item-price">Per Total: ₹{product.productPrice * product.productQuantity}</div>

                        <span className={`product-status ${product.productIsShipped ? 'shipped' : 'pending'}`}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            {product.productIsShipped ? (
                              <path d="M8 12L11 15L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            ) : (
                              <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            )}
                          </svg>
                          {product.productIsShipped ? 'Shipped' : 'Processing'}
                        </span>
                      </div>
                      <div className="item-actions">
                        <button 
                          className="action-btn view-btn"
                          onClick={() => navigate(`/view-product-details/${product.productId}`)}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          View Details
                        </button>
                     
                       
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-products">
                  No product details available for this order
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
  );
}

export default Orders;